import { Component, inject, Input, OnInit } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import { AuthService } from "@core/services/auth.service";
import { Address, countryDefaultValue } from "@models/address/address";
import { Trust } from "@models/trusts/trust";
import { jurisdictionOptions, JurisdictionType } from "@models/trusts/trusts-enums";
import { SelectOption } from "@models/ui-models/select-options";
import { NgbActiveModal, NgbDateStruct } from "@ng-bootstrap/ng-bootstrap";
import {
  CommonModalWrapperComponent
} from "@shared/components/commons/common-modal-wrapper/common-modal-wrapper.component";
import { DividerComponent } from "@shared/components/commons/divider/divider.component";
import { DatePickerComponent } from "@shared/controls/date-picker/date-picker.component";
import { FlagsAutocompleteComponent } from "@shared/controls/flags-autocomplete/flags-autocomplete.component";
import { InputComponent } from "@shared/controls/input/input.component";
import {
  AbnMaskedInputComponent
} from "@shared/controls/masked-input/specific-masked-inputs/abn-masked-input/abn-masked-input.component";
import {
  MultipleInputDatepickerComponent
} from "@shared/controls/multiple-input-datepicker/multiple-input-datepicker.component";
import { SelectComponent } from "@shared/controls/select/select.component";
import { TrustsApiService } from "@shared/services/trusts-api.service";
import { CustomFormValidators } from "@shared/validators/custom-form-validators";
import { DatePickerFormValidators, DatepickerHelper } from "@shared/validators/date-picker-form-validators";
import { observe, Observer } from "fast-json-patch";
import { ToastrService } from "ngx-toastr";
import { finalize } from "rxjs";
import {
  AddressFormGroup,
  AddressFormGroupComponent
} from "../../../../../../forms/reusable-form-groups/address-form-group/address-form-group.component";

export interface EditTrustDetailsFormControls {
  name: FormControl<string | null>;
  entityNumber: FormControl<string | null>;
  dateOfEstablishment: FormControl<Date | null>; // Commencement Date
  jurisdiction: FormControl<JurisdictionType | null>;
  registeredAddress: AddressFormGroup,
  variationDates: FormControl<Date[] | null>,
  vestingDate: FormControl<Date | null>;
  partnerManagerId: FormControl<string | null>;
  accountManagerId: FormControl<string | null>;
  referenceNumber: FormControl<string | null>;
  familyGroup: FormControl<string | null>;
}

export type EditTrustDetailsFormGroup = FormGroup<EditTrustDetailsFormControls>;

@Component({
  selector: 'app-form-edit-trust-details',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    CommonModalWrapperComponent,
    DividerComponent,
    InputComponent,
    SelectComponent,
    AbnMaskedInputComponent,
    DatePickerComponent,
    MultipleInputDatepickerComponent,
    AddressFormGroupComponent,
    FlagsAutocompleteComponent,
  ],
  templateUrl: './form-edit-trust-details.component.html',
  styleUrl: './form-edit-trust-details.component.scss'
})
export class FormEditTrustDetailsComponent implements OnInit {
  private trustsApiService = inject(TrustsApiService);
  private authService = inject(AuthService);
  private activeModal = inject(NgbActiveModal);
  private toastr = inject(ToastrService);

  @Input() trustProfile!: Trust;

  readonly jurisdictionOptions = jurisdictionOptions;
  readonly vestingDateCustomErrors = {
    ...DatePickerFormValidators.errorMessages,
    tooSmallDate: 'Date of Vesting cannot be earlier or same as Commencement Date'
  };
  usersOption: SelectOption[] = [];
  observer!: Observer<Trust>;
  isLoading = false;

  minCommencementDate: NgbDateStruct = DatepickerHelper.getStructFromString('01-01-1920');
  minDate!: NgbDateStruct;
  maxDate: NgbDateStruct = DatepickerHelper.getNextNYearsStruct(100);

  form: EditTrustDetailsFormGroup = new FormGroup({
    name: new FormControl<string | null>(null, [Validators.required, CustomFormValidators.maxLength(50)]),
    entityNumber: new FormControl<string | null>(null),
    dateOfEstablishment: new FormControl<Date | null>(null),      // Commencement Date
    jurisdiction: new FormControl<JurisdictionType | null>(null, [Validators.required]),
    registeredAddress: AddressFormGroupComponent.defineForm(),
    variationDates: new FormControl<Date[] | null>([]),
    vestingDate: new FormControl<Date | null>(null),
    partnerManagerId: new FormControl<string | null>(null),
    accountManagerId: new FormControl<string | null>(null),
    referenceNumber: new FormControl<string | null>(null),
    familyGroup: new FormControl<string | null>(null),
  });

  ngOnInit(): void {
    this.minDate = this.trustProfile.dateOfEstablishment ? DatepickerHelper.getStructFromDate(this.trustProfile.dateOfEstablishment) : this.minCommencementDate;
    this.usersOption = (this.authService.currentUserProfile()?.users || []).map(user => {
      return {
        value: user.id,
        label: user.fullName
      };
    });
    this.setupChange();
  }

  setupChange(): void {
    this.form.patchValue({
      ...this.trustProfile,
      registeredAddress: new Address({
        ...this.trustProfile.registeredAddress,
        country: this.trustProfile.registeredAddress.country || countryDefaultValue
      })
    });
  }

  save(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const currentTrust = new Trust(structuredClone(this.trustProfile));
    this.observer = observe<Trust>(currentTrust);

    Object.keys(this.form.controls).forEach((key) => {
      currentTrust[key] = this.form.controls[key].value;
    });

    this.isLoading = true;
    this.form.disable();

    this.trustsApiService.patchTrust(this.trustProfile.entityId, this.observer)
      .pipe(finalize(() => {
        this.isLoading = false;
        this.form.enable();
      }))
      .subscribe({
        next: (updatedTrust) => {
          this.activeModal.close(updatedTrust);
        },
        error: (error) => {
          console.warn('[patchTrust] error: ', error);
          this.toastr.error('Please fill required fields', 'Error');
        }
      });
  }

  cancel(): void {
    this.activeModal.dismiss();
  }

  get defaultMinDateOrCommencementDate(): NgbDateStruct {
    return this.form.controls.dateOfEstablishment.value
      ? DatepickerHelper.getStructFromDate(this.form.controls.dateOfEstablishment.value)
      : this.minDate;
  }

  get defaultMinDateOrDayAfterCommencementDate(): NgbDateStruct {
    return this.form.controls.dateOfEstablishment.value
      ? DatepickerHelper.getStructFromDateDayAfter(this.form.controls.dateOfEstablishment.value)
      : this.minDate;
  }

  get defaultMaxDateOrVestingDate(): NgbDateStruct {
    return this.form.controls.vestingDate.value
      ? DatepickerHelper.getStructFromDate(this.form.controls.vestingDate.value)
      : this.maxDate;
  }

  get vestingDateErrors() {
    return this.defaultMinDateOrCommencementDate === this.minCommencementDate
      ? DatePickerFormValidators.errorMessages :
      this.vestingDateCustomErrors;
  }
}
